import { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';

/**
 * Custom hook for managing carousel animations with parallax effects and dynamic height
 * @param {Object} options - Configuration options
 * @param {boolean} options.loop - Whether the carousel should loop (default: false)
 * @param {string} options.align - Slide alignment: 'start' | 'center' | 'end' (default: 'center')
 * @param {boolean} options.draggable - Whether slides can be dragged (default: true)
 * @param {number} options.parallaxAmount - Parallax effect strength (default: 50)
 * @param {number} options.fadeSpeed - How quickly outgoing slides fade (default: 2.2)
 * @returns {Object} Carousel state and controls
 */
function useCarouselAnimation({
    align = 'center',
    draggable = true,
    fadeSpeed = 2.2,
    loop = false,
    parallaxAmount = 50,
} = {}) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [slideHeights, setSlideHeights] = useState([]);
    const [slideStyles, setSlideStyles] = useState([]);
    const slidesContainerRef = useRef(null);
    const [emblaRef, emblaApi] = useEmblaCarousel({ align, draggable, loop });

    // Measure all slides on mount and after images load
    const measureSlides = useCallback(() => {
        if (!slidesContainerRef.current) return;
        const slides = slidesContainerRef.current.querySelectorAll('[data-slide]');
        const heights = Array.from(slides).map((slide) => slide.offsetHeight);
        setSlideHeights(heights);
    }, []);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    // Calculate slide styles based on scroll progress
    const updateSlideStyles = useCallback(() => {
        if (!emblaApi) return;
        const scrollProgress = emblaApi.scrollProgress();
        const slidesCount = emblaApi.slideNodes().length;
        const styles = emblaApi.slideNodes().map((_, index) => {
            // Calculate how far this slide is from the "center" of the viewport
            const slideProgress = index / (slidesCount - 1) - scrollProgress;
            const distance = Math.abs(slideProgress);

            // Opacity: incoming slides stay at 1, outgoing slides fade to 0
            let opacity = 1;
            if (slideProgress < 0) {
                opacity = Math.max(0, 1 - distance * fadeSpeed);
            }

            // Z-index: slides ahead (positive slideProgress) are on top
            const zIndex = slideProgress >= 0 ? 10 : 1;

            // Parallax effect: outgoing slides move slower (lag behind)
            let translateX = 0;
            if (slideProgress < 0) {
                translateX = -slideProgress * parallaxAmount;
            }

            return { opacity, translateX, zIndex };
        });
        setSlideStyles(styles);
    }, [emblaApi, fadeSpeed, parallaxAmount]);

    useEffect(() => {
        if (!emblaApi) return undefined;
        onSelect();
        updateSlideStyles();
        emblaApi.on('select', onSelect);
        emblaApi.on('scroll', updateSlideStyles);
        emblaApi.on('reInit', updateSlideStyles);
        return () => {
            emblaApi.off('select', onSelect);
            emblaApi.off('scroll', updateSlideStyles);
            emblaApi.off('reInit', updateSlideStyles);
        };
    }, [emblaApi, onSelect, updateSlideStyles]);

    // Measure slides after mount and when images load
    useEffect(() => {
        const timer = setTimeout(measureSlides, 100);
        window.addEventListener('resize', measureSlides);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', measureSlides);
        };
    }, [measureSlides]);

    const currentHeight = slideHeights[selectedIndex] || 'auto';

    const scrollNext = useCallback(() => {
        emblaApi?.scrollNext();
    }, [emblaApi]);

    const scrollTo = useCallback(
        (index) => {
            emblaApi?.scrollTo(index);
        },
        [emblaApi]
    );

    const getSlideStyle = useCallback(
        (index) => {
            const style = slideStyles[index] || { opacity: 1, translateX: 0, zIndex: 1 };
            return {
                opacity: style.opacity,
                transform: `translateX(${style.translateX}%)`,
                transition: 'opacity 100ms ease-out',
                zIndex: style.zIndex,
            };
        },
        [slideStyles]
    );

    return {
        currentHeight,
        emblaApi,
        emblaRef,
        getSlideStyle,
        scrollNext,
        scrollTo,
        selectedIndex,
        slidesContainerRef,
    };
}

export default useCarouselAnimation;
